/**
 * BHL pre-launch storefront changes (P210, Nathan Keeble 2026-10-26; Neil Bumford's amendments of 22 October).
 * Loaded as a module (it runs once however many sections include it) by sections/bhl-enquiry-strip.liquid on every
 * page and by each P210 snippet that needs it.
 *
 *   1. Enquiry dialogs (snippets/bhl-enquiry-dialog.liquid): any element with data-bhl-dialog-open="<dialog id>"
 *      opens that native dialog as a modal; the close button, Escape and a click on the backdrop close it, and focus
 *      returns to the opener. Without the dialog on the page (or without JavaScript) the opener stays a plain link.
 *   2. After a send: Shopify returns to the page with ?contact_posted=true and the form id as the fragment. Only that
 *      form shows its thank-you (or error) line, and its dialog reopens so the shopper sees it.
 *   3. bhl-stock-info: the product page "In stock" button and its information popup (snippets/bhl-stock-info.liquid).
 *   4. In stock first (interim, snippets/bhl-stock-view.liquid): a shopper's click on a plain category link
 *      (/collections/<handle> with no query) opens the category's in-stock view (?filter.v.availability=1). Links in
 *      the HTML are not changed, so search engines still crawl the plain address with the full list (P176). Ctrl, Cmd,
 *      Shift and middle clicks are left alone. A category where the shopper chose "Show all" is not redirected again
 *      during the visit (sessionStorage, which may be unavailable: then nothing is remembered).
 *      Categories over 5,000 products (no Availability filter there) have an in-stock twin collection instead
 *      (<handle>-in-stock, snippets/bhl-instock-twins.liquid prints the live ones as JSON): a click on such a
 *      category opens its twin. Links to a twin itself are never redirected.
 *   5. bhl-stock-view: removes an in-stock parameter Shopify cannot use (no Availability filter over 5,000 products,
 *      or a landing page without a grid) from the address bar without reloading, or, on a category with a twin,
 *      opens the twin instead; an empty in-stock view (or an empty twin) opens the full list instead.
 *   6. bhl-oos-alt-fetch: on a sold-out product, fetches in-stock alternatives from the category's in-stock view, then
 *      its parent's, through the Section Rendering API (sections/bhl-oos-alt-grid.liquid).
 *   7. Round 6, forms hardening (Nathan Keeble, 2026-10-29): the enquiry forms, the contact page form and the email
 *      signup. The rules are native constraints in the markup (type, required, pattern, maxlength), so the browser
 *      never fires submit while a field is invalid, with or without this script. Here the browser's bubbles become
 *      inline errors: aria-invalid, an error line joined to aria-describedby, focus on the first invalid field. A
 *      filled honeypot (data-bhl-hp) blocks the send silently. A web address typed without https:// gets it added.
 */

const STOCK_PARAM = 'filter.v.availability';
const SKIP_KEY = 'bhl-stock-all:';
const SKIP_HANDLES = new Set(['all', 'vendors', 'types', 'frontpage']);
const ALLOWED_PARAMS = new Set(['preview_theme_id']);
const TWIN_SUFFIX = '-in-stock';
let twinCache = null;

function store(fn, fallback) {
  try {
    return fn(window.sessionStorage);
  } catch (_) {
    return fallback;
  }
}

/** The collection handle of a plain category address on this site, or null. */
export function plainCollectionHandle(href, base) {
  let url;
  try {
    url = new URL(href, base);
  } catch (_) {
    return null;
  }
  if (base && url.origin !== new URL(base).origin) return null;
  const m = url.pathname.match(/^\/collections\/([^/]+)\/?$/);
  if (!m) return null;
  for (const key of url.searchParams.keys()) {
    if (!ALLOWED_PARAMS.has(key)) return null;
  }
  const handle = decodeURIComponent(m[1]).toLowerCase();
  if (SKIP_HANDLES.has(handle) || handle.endsWith(TWIN_SUFFIX)) return null;
  return handle;
}

/** {parent handle: twin path} from the JSON printed by snippets/bhl-instock-twins.liquid; {} when absent or unreadable. */
export function parseTwins(text) {
  try {
    const map = JSON.parse(text || '{}');
    if (!map || typeof map !== 'object' || Array.isArray(map)) return {};
    const out = {};
    for (const [handle, path] of Object.entries(map)) {
      if (typeof path === 'string' && /^\/[^/]/.test(path)) out[handle.toLowerCase()] = path;
    }
    return out;
  } catch (_) {
    return {};
  }
}

function twins() {
  if (twinCache === null) {
    const el = typeof document !== 'undefined' ? document.getElementById('BhlInstockTwins') : null;
    twinCache = parseTwins(el ? el.textContent : '');
  }
  return twinCache;
}

/** The twin's address for a plain category address, keeping the preview parameter of the original link. */
export function twinHref(twinPath, href, base) {
  const from = new URL(href, base);
  const url = new URL(twinPath, base);
  for (const [key, value] of from.searchParams) {
    if (ALLOWED_PARAMS.has(key)) url.searchParams.set(key, value);
  }
  url.hash = '';
  return url.toString();
}

/** Where a click on a plain category link goes: its twin when it has one, else Shopify's in-stock view. */
export function stockTarget(handle, href, base, map) {
  const twin = (map || {})[handle];
  return twin ? twinHref(twin, href, base) : inStockHref(href, base);
}

/** The in-stock view address for a plain category address. */
export function inStockHref(href, base) {
  const url = new URL(href, base);
  url.searchParams.set(STOCK_PARAM, '1');
  url.hash = '';
  return url.toString();
}

function skipped(handle) {
  return store((s) => s.getItem(SKIP_KEY + handle) === '1', false);
}

function setSkip(handle, on) {
  store((s) => (on ? s.setItem(SKIP_KEY + handle, '1') : s.removeItem(SKIP_KEY + handle)), null);
}

/* ---------------------------------------------------------------------------------------------- 1 and 4: clicks */
let lastOpener = null;

function onClick(event) {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return;
  }
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const opener = target.closest('[data-bhl-dialog-open]');
  if (opener) {
    const dialog = document.getElementById(opener.getAttribute('data-bhl-dialog-open') || '');
    if (dialog instanceof HTMLDialogElement && typeof dialog.showModal === 'function') {
      event.preventDefault();
      lastOpener = opener;
      wireDialog(dialog);
      if (!dialog.open) dialog.showModal();
      return;
    }
  }

  const closer = target.closest('[data-bhl-dialog-close]');
  if (closer) {
    const dialog = closer.closest('dialog');
    if (dialog) {
      event.preventDefault();
      dialog.close();
    }
    return;
  }

  const link = target.closest('a[href]');
  if (!link || (link.target && link.target !== '_self') || link.hasAttribute('download')) return;
  if (link.hasAttribute('data-bhl-stock-all')) {
    const view = link.closest('bhl-stock-view');
    if (view?.dataset.handle) setSkip(view.dataset.handle, true);
    return;
  }
  if (link.hasAttribute('data-bhl-stock-only')) {
    const view = link.closest('bhl-stock-view');
    if (view?.dataset.handle) setSkip(view.dataset.handle, false);
    return;
  }
  const handle = plainCollectionHandle(link.getAttribute('href') || '', location.href);
  if (!handle || skipped(handle)) return;
  event.preventDefault();
  location.assign(stockTarget(handle, link.href, location.href, twins()));
}

/** a click on the backdrop (the dialog element itself, outside its inner box) closes it */
function onDialogClick(event) {
  const dialog = event.currentTarget;
  if (event.target === dialog) dialog.close();
}

/**
 * Gate round 1, condition 2. Shopify's hCaptcha draws its challenge in the page body, which a modal dialog makes inert,
 * so on submit the dialog is reopened as a non-modal. close() queues its close event as a task (and Chrome was observed
 * not to fire it at all when the dialog is shown again in the same task), so no flag cleared in the submit task can
 * describe it. The close listener decides from the dialog itself (consumeClose): a close event that finds the dialog
 * open again belongs to this reopen and never moves focus; a real close (Escape, close button, backdrop) finds it
 * closed. Focus is put back on the submit button that was pressed.
 */
export function reopenNonModal(dialog, submitter) {
  dialog.close();
  dialog.show();
  if (submitter && typeof submitter.focus === 'function') submitter.focus({ preventScroll: true });
}

/** For the close listener: true (return focus to the opener) only when the dialog is really closed. */
export function consumeClose(dialog) {
  return !dialog.open;
}

function wireDialog(dialog) {
  if (dialog.dataset.bhlWired) return;
  dialog.dataset.bhlWired = '1';
  dialog.addEventListener('click', onDialogClick);
  dialog.addEventListener('close', () => {
    if (!consumeClose(dialog)) return; // the close made by reopenNonModal: keep focus in the form
    if (lastOpener && document.contains(lastOpener)) lastOpener.focus();
    lastOpener = null;
  });
  // a non-modal dialog (after submit) does not close on Escape by itself
  dialog.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && dialog.open && !dialog.matches(':modal')) dialog.close();
  });
}

/* ------------------------------------------------------------------------------------------ 2: after a send */
function showPosted() {
  const params = new URLSearchParams(location.search);
  const id = decodeURIComponent(location.hash.replace(/^#/, ''));
  const form = id && /^BhlEnquiry-/.test(id) ? document.getElementById(id) : null;
  document.querySelectorAll('.bhl-enquiry__form').forEach((f) => {
    if (f !== form) f.querySelectorAll('[data-bhl-posted], [data-bhl-error]').forEach((el) => el.remove());
  });
  if (!(form instanceof HTMLFormElement)) return;
  const notice = form.querySelector(params.get('contact_posted') === 'true' ? '[data-bhl-posted]' : '[data-bhl-error]') ||
    form.querySelector('[data-bhl-error]');
  if (!notice) return;
  notice.hidden = false;
  if (notice.hasAttribute('data-bhl-posted')) form.classList.add('bhl-enquiry__form--sent');
  const dialog = form.closest('dialog');
  if (dialog instanceof HTMLDialogElement && typeof dialog.showModal === 'function' && !dialog.open) {
    wireDialog(dialog);
    dialog.showModal();
  } else form.scrollIntoView({ block: 'center' });
  notice.focus({ preventScroll: Boolean(dialog) });
}

/* ------------------------------------------------------------------------------------------ 3: stock info */
class BhlStockInfo extends HTMLElement {
  #listen = null;

  connectedCallback() {
    this.button = this.querySelector('.bhl-stock-info__button');
    this.panel = this.querySelector('.bhl-stock-info__panel');
    if (!this.button || !this.panel) return;
    this.#listen = new AbortController();
    const signal = this.#listen.signal;
    this.button.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggle(this.panel.hidden);
    }, { signal });
    this.querySelector('[data-bhl-stock-info-close]')?.addEventListener('click', () => this.toggle(false, true), { signal });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.panel.hidden) {
        e.stopPropagation();
        this.toggle(false, true);
      }
    }, { signal });
    document.addEventListener('click', (e) => {
      if (!this.panel.hidden && e.target instanceof Node && !this.contains(e.target)) this.toggle(false);
    }, { signal });
  }

  disconnectedCallback() {
    this.#listen?.abort();
  }

  toggle(open, refocus = false) {
    this.panel.hidden = !open;
    this.button.setAttribute('aria-expanded', String(open));
    this.toggleAttribute('open', open);
    if (open) this.panel.querySelector('a, button')?.focus({ preventScroll: true });
    else if (refocus) this.button.focus();
  }
}

/* ------------------------------------------------------------------------------------------ 5: stock view */
class BhlStockView extends HTMLElement {
  connectedCallback() {
    const state = this.dataset.state;
    const handle = this.dataset.handle;
    const url = new URL(location.href);
    if (state === 'twin-parent' && url.searchParams.has(STOCK_PARAM) && this.dataset.twinUrl) {
      // a category with an in-stock twin, reached through an in-stock address Shopify cannot filter: open the twin
      location.replace(twinHref(this.dataset.twinUrl, location.href, location.href));
    } else if ((state === 'unavailable' || state === 'twin-parent') && url.searchParams.has(STOCK_PARAM)) {
      url.searchParams.delete(STOCK_PARAM);
      history.replaceState(history.state, '', url.toString());
    } else if (state === 'empty' && this.dataset.allUrl) {
      if (handle) setSkip(handle, true);
      location.replace(this.dataset.allUrl);
    }
  }
}

/* ------------------------------------------------------------------------------------------ 6: alternatives */
class BhlOosAltFetch extends HTMLElement {
  #done = false;

  connectedCallback() {
    if (this.#done) return;
    this.#done = true;
    this.load();
  }

  async load() {
    const urls = (this.dataset.urls || '').split(' ').filter(Boolean);
    const links = (this.dataset.links || '').split('||').map((pair) => pair.split('|'));
    const exclude = this.dataset.exclude || '';
    const max = Math.max(1, Math.min(4, Number(this.dataset.max) || 4));
    for (let i = 0; i < urls.length; i++) {
      let items = [];
      try {
        const response = await fetch(urls[i], { headers: { Accept: 'text/html' } });
        if (!response.ok) continue;
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        items = [...doc.querySelectorAll('.bhl-oos-alt-grid .bhl-oos-alt__item')].filter(
          (li) => li.getAttribute('data-product-id') !== exclude
        );
      } catch (_) {
        continue;
      }
      if (!items.length) continue;
      this.draw(items.slice(0, max), links[i] || []);
      return;
    }
  }

  draw(items, [href, title]) {
    const id = `BhlOosAlt-${this.dataset.blockId || 'fetch'}`;
    const heading = document.createElement('h2');
    heading.id = id;
    heading.className = 'bhl-oos-alt__heading h6';
    heading.textContent = this.dataset.heading || 'In stock alternatives';
    const list = document.createElement('ul');
    list.className = 'bhl-oos-alt__list';
    list.setAttribute('role', 'list');
    for (const li of items) list.append(document.importNode(li, true));
    this.replaceChildren(heading, list);
    if (this.dataset.linkPrefix && href && title) {
      const more = document.createElement('p');
      more.className = 'bhl-oos-alt__more';
      const a = document.createElement('a');
      a.href = href;
      a.textContent = `${this.dataset.linkPrefix} ${title}`;
      more.append(a);
      this.append(more);
    }
    this.hidden = false;
  }
}

/* ------------------------------------------------------------------------------------------ 7: form validation */
export const FORM_SELECTOR = '.bhl-enquiry__form, .contact-form__form, .email-signup__form';
// the same rules as the pattern attributes in the markup (the test checks they agree)
const PHONE_RE = /^[ ]*\+?[ ()-]*(?:[0-9][ ()-]*){10,15}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** An optional phone number: empty, or digits, spaces, +, ( ) and - only with 10 to 15 digits (+ only first). */
export function isPhone(value) {
  const v = String(value ?? '');
  return v === '' || PHONE_RE.test(v); // as the pattern attribute: only an empty value skips the rule
}

/** local@domain.tld with no spaces (the browser's type="email" check applies as well). */
export function isEmail(value) {
  return EMAIL_RE.test(String(value ?? ''));
}

/** A web address typed without a scheme ("www.example.co.uk/x") gets https://; anything else is left as typed. */
export function withScheme(value) {
  const v = String(value ?? '').trim();
  if (!v || /^[a-z][a-z0-9+.-]*:\/\//i.test(v)) return v;
  const bare = v.replace(/^\/\//, '');
  return /^[^\s/:]+\.[^\s/]+/.test(bare) ? `https://${bare}` : v;
}

/** The inline message for a field from its ValidityState; '' when valid. */
export function messageFor(field) {
  const v = field.validity || {};
  if (v.valid) return '';
  if (v.valueMissing) return (field.dataset && field.dataset.bhlMissing) || 'Please fill in this field.';
  if (v.tooLong) return `Use no more than ${field.maxLength} characters.`;
  switch (field.type) {
    case 'email':
      return 'Enter an email address like name@example.co.uk, with no spaces.';
    case 'tel':
      return 'Enter a phone number of 10 to 15 digits, using only digits, spaces, +, ( ) and -.';
    case 'url':
      return 'Enter the full web address, for example https://www.example.co.uk/product.';
    case 'number':
      if (v.rangeUnderflow) return `Enter ${field.min} or more.`;
      if (v.rangeOverflow) return `Enter ${field.max} or less.`;
      return 'Enter a whole number.';
    default:
      return 'Check this field.';
  }
}

/** What a submit event may do: 'drop' (honeypot filled: silently), 'block' (a field is invalid) or 'send'. */
export function submitVerdict(honeypotValue, invalidCount) {
  if (honeypotValue) return 'drop';
  return invalidCount > 0 ? 'block' : 'send';
}

function managedForm(el) {
  const form = el && el.form;
  return form instanceof HTMLFormElement && form.matches(FORM_SELECTOR) ? form : null;
}

function checkedFields(form) {
  return [...form.elements].filter((el) => el.willValidate && !el.hasAttribute('data-bhl-hp'));
}

function describe(field, id, on) {
  const ids = (field.getAttribute('aria-describedby') || '').split(/\s+/).filter((x) => x && x !== id);
  if (on) ids.push(id);
  if (ids.length) field.setAttribute('aria-describedby', ids.join(' '));
  else field.removeAttribute('aria-describedby');
}

function errorLine(field, create) {
  const id = `${field.id || field.name}-error`;
  // looked up inside the field's own form only: two forms on a page never share or clear each other's lines
  const scope = field.form || document;
  let line = [...scope.querySelectorAll('.bhl-field-error')].find((el) => el.id === id) || null;
  if (!line && create) {
    line = document.createElement('p');
    line.id = id;
    line.className = 'bhl-field-error';
    line.hidden = true;
    const box = field.closest('.bhl-enquiry__field');
    const row = field.closest('.contact-form__form-row');
    if (box) box.append(line);
    else if (row) {
      // Horizon's contact form puts name and email side by side: the line goes under the row, named after its field
      const label = field.labels && field.labels[0] ? field.labels[0].textContent.replace(/\*/g, '').trim() : '';
      if (label) line.dataset.field = label;
      row.after(line);
    } else (field.closest('.email-signup__input-group') || field).after(line);
  }
  return line;
}

/** Show (or clear) the field's inline error; true when the field is valid. */
function showState(field) {
  const msg = messageFor(field);
  const line = errorLine(field, Boolean(msg));
  if (msg) {
    field.setAttribute('aria-invalid', 'true');
    line.textContent = line.dataset.field ? `${line.dataset.field}: ${msg}` : msg;
    line.hidden = false;
    describe(field, line.id, true);
  } else {
    field.removeAttribute('aria-invalid');
    if (line) {
      line.textContent = '';
      line.hidden = true;
      describe(field, line.id, false);
    }
  }
  return !msg;
}

function addScheme(form) {
  form.querySelectorAll('input[type="url"]').forEach((input) => {
    const fixed = withScheme(input.value);
    if (fixed !== input.value) input.value = fixed;
  });
}

/** The browser fires invalid (it does not bubble) at each invalid field, in order, when a send is tried. */
export function onInvalid(event) {
  const field = event.target;
  const form = managedForm(field);
  if (!form) return;
  event.preventDefault(); // the inline error replaces the browser's bubble; the send stays blocked
  if (field.hasAttribute('data-bhl-hp')) return; // silently
  showState(field);
  const first = checkedFields(form).find((el) => !el.validity.valid);
  if (first === field) field.focus();
}

/** Registered on window capture at module start, so it runs before the dialog's document listener (not before a
 * listener registered earlier on window capture): a filled honeypot or an invalid field stops the send. */
export function onSubmitGuard(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches(FORM_SELECTOR)) return;
  const hp = form.querySelector('[data-bhl-hp]');
  const bad = checkedFields(form).filter((el) => !el.validity.valid);
  const verdict = submitVerdict(hp ? hp.value : '', bad.length);
  if (verdict === 'send') return;
  event.preventDefault();
  event.stopImmediatePropagation();
  if (verdict === 'block') {
    bad.forEach(showState);
    bad[0].focus();
  }
}

function onFieldEvent(event) {
  const field = event.target;
  const form = managedForm(field);
  if (!form || field.hasAttribute('data-bhl-hp') || !field.willValidate) return;
  if (event.type === 'change' && field.type === 'url') {
    const fixed = withScheme(field.value);
    if (fixed !== field.value) field.value = fixed;
  }
  // on leaving a filled field, or while correcting one already marked: never on tabbing past an empty field
  if (field.getAttribute('aria-invalid') === 'true' || (event.type !== 'input' && field.value !== '')) showState(field);
}

/* ------------------------------------------------------------------------------------------ start */
if (typeof window !== 'undefined' && !window.__bhlP210) {
  window.__bhlP210 = true;
  if (!customElements.get('bhl-stock-info')) customElements.define('bhl-stock-info', BhlStockInfo);
  if (!customElements.get('bhl-stock-view')) customElements.define('bhl-stock-view', BhlStockView);
  if (!customElements.get('bhl-oos-alt-fetch')) customElements.define('bhl-oos-alt-fetch', BhlOosAltFetch);
  // bubble phase: a menu that opens a submenu on click (and calls preventDefault) keeps working
  document.addEventListener('click', onClick);
  // round 6: a submit click (also the synthetic click of an Enter press) adds https:// before the browser validates
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target instanceof Element ? event.target.closest('button, input[type="submit"]') : null;
      const form = button && button.type === 'submit' ? managedForm(button) : null;
      if (form) addScheme(form);
    },
    true
  );
  document.addEventListener('invalid', onInvalid, true);
  document.addEventListener('focusout', onFieldEvent, true);
  document.addEventListener('input', onFieldEvent, true);
  document.addEventListener('change', onFieldEvent, true);
  window.addEventListener('submit', onSubmitGuard, true);
  // Shopify protects the contact form with hCaptcha, whose challenge is drawn in the page body. A modal dialog makes
  // the rest of the page inert and sits in the top layer above it, so on submit the dialog is reopened as a non-modal
  // (still visible, styled the same) and a challenge, when one is needed, can be seen and answered.
  document.addEventListener(
    'submit',
    (event) => {
      const dialog = event.target instanceof Element ? event.target.closest('dialog.bhl-dialog') : null;
      if (!(dialog instanceof HTMLDialogElement) || !dialog.open || !dialog.matches(':modal')) return;
      wireDialog(dialog);
      reopenNonModal(dialog, event.submitter);
    },
    true
  );
  const start = () => showPosted();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
}
